<template lang="pug">
div(ref="hostRef")
	div(ref="topToScroll")
	div.tw-flex.tw-flex-col.tw-z-50.tw-p-4.tw-bg-white.tw-rounded-lg.tw-shadow-md(
		class="tw-top-[-1px] tw-mb-[24px] emd:tw-py-2"
		:class="{'tw-sticky': !bookingStore.isPostSale}"
		ref="header"
	)
		div.tw-flex.tw-flex-row.tw-pb-0.tw-gap-3(v-if="isMobile")
			img(v-if="!bookingStore.isPostSale" src="/img/Trenitalia_logo.svg" style="height: 30px;")
			span(v-if="bookingStore.isPostSale" width="150px" class="tw-basis-1/12")
			div.tw-flex-1.tw-flex.tw-justify-end
				stepper-view(:mobile-style="isMobile" ref="mobileSteps")

		div.tw-flex.tw-flex-row.tw-items-center.tw-flex-row(id="desktop-stepper-container" ref="desktopStepperContainer" )
			img(v-if="!bookingStore.isPostSale && !isMobile" src="/img/Trenitalia_logo.svg" style="height: 50px;")
			span(v-if="bookingStore.isPostSale" width="150px" class="tw-basis-1/12")
			div.tw-flex-1.tw-flex.tw-justify-end.tw-items-center(class="emd:tw-justify-center")
				stepper-view(ref="desktopSteps")
		brief

	v-window(v-model="stepsHook")
		v-window-item(value="search")
			search(class="tw-mt-[24px]")
		v-window-item(value="travel-solutions")
			travel-solutions(@scroll-to-top="scrollToTop()")
		v-window-item(value="return-travel-solutions")
			travel-solutions(is-return @scroll-to-top="scrollToTop()")
		v-window-item(value="carnet-solutions")
			carnet-solutions(v-if="['carnet-solutions', 'buyer-data'].includes(stepper.step)")
		v-window-item(value="seat-selection")
			seat-selection(v-if="stepper.inSeatSelectionPage" @next-to-travellers="stepper.goNext()" @back="backFromSeatSelection")
		v-window-item(value="travellers")
			travellers-data(@next-to-travellers="stepper.goNext()" @back="backFromSeatSelection")

	v-alert.tw-mt-14(
			v-model="bookingAppSettingsStore.alert.show"
			border="top"
			closable
			outlined
			:type="bookingAppSettingsStore.alert.type"
			transition="slide-x-reverse-transition"
	) {{ bookingAppSettingsStore.alert.message }}

	div(id="booking-footer")
	dialog-manager
</template>

<script setup>
import {useBookingStore} from '@/stores/booking';
import { useBookingAppSettingStore } from '@/stores/booking-app-setting';
import { useBookingStepperStore } from '@/stores/booking-stepper';
import {computed, defineAsyncComponent, onActivated, onMounted, provide, ref, watch} from 'vue';
import {useSticky} from '@/composables/sticky';
import { useI18n } from 'vue-i18n';
import { loadSearchSettings } from '@/common-scripts/dev-helper';
import {useDisplay} from "vuetify";
import Search from './components/tab1_search/search.vue';
import DialogManager from "@/common-components/dialog-manager.vue";
import CarnetSolutions from "@/sale/components/tab2_travel_solutions/carnet/carnetSolutions.vue";
import StepperView from '@/sale/components/header-bar/stepper.vue';
import Brief from "@/sale/components/header-bar/brief.vue";


const TravelSolutions = defineAsyncComponent(() => import("@/sale/components/tab2_travel_solutions/travelSolutions.vue"));
const SeatSelection = defineAsyncComponent(() => import("@/sale/components/tab2b_seat_selection/seatSelection.vue"));
const TravellersData = defineAsyncComponent(() => import("@/sale/components/tab3_travellers-data-section/travellers-data.vue"));
const stepper = useBookingStepperStore();
const bookingAppSettingsStore = useBookingAppSettingStore();
const bookingStore = useBookingStore();
const header = ref();
const hostRef = ref();
const {sticking: isHeaderSticking} = useSticky(header);
const props = defineProps({
	siteLogoSrc: String,
	lang: String
});
const topToScroll = ref();
const stepsHook = computed(() => {
	if (!stepper.step) return ;
	if (stepper.inSearchPage) {
		return 'search';
	} else if (['travel-solutions', 'forward-travel-solutions'].includes(stepper.step)) {
		return 'travel-solutions';
	} else if (stepper.inSeatSelectionPage) {
		return 'seat-selection';
	} else if (stepper.inCarnetPages) {
		return 'carnet-solutions';
	}

	return stepper.step;
});
const {height, sm, md, xs} = useDisplay();
const isMobile = ref();
const desktopStepperContainer = ref();
const desktopSteps = ref();
const mobileSteps = ref();
const i18n = useI18n();
i18n.locale.value = props.lang || import.meta.env.VITE_LANG || 'en';


function backFromSeatSelection() {
	bookingStore.setSeatSelection();
	stepper.goBack();
}
function hideMobileSteps(steps) {
	for(const step of steps) {
		step.classList.add('hide')
	}
}
async function animateSteps(desktopSteps, mobileSteps, i = 0) {
	if (i >= desktopSteps.length) {
		desktopStepperContainer.value.classList.add('remove');
		return;
	}

	desktopSteps[i].classList.add('scaleOutTop');

	setTimeout(() => {
		mobileSteps[i].classList.remove('hide');
		mobileSteps[i].classList.add('scaleInForwardCenter');
		animateSteps(desktopSteps, mobileSteps, i + 1);
	}, 500);
}
function scrollToTop(){
	if(topToScroll.value){
		topToScroll.value.scrollIntoView({ behavior: 'smooth', block: 'start' });
	}
}
function setMobileParameter() {
	isMobile.value = (xs.value || sm.value || md.value) ? true : false;
}



watch(() => stepper.step, () => scrollToTop());
watch(() => height.value, () => setMobileParameter());


setMobileParameter();
loadSearchSettings();


provide('isMobile', isMobile)


window.addEventListener('popstate', function (event) {
	if(stepper && stepper.stepsCompleted > 0) {
		history.replaceState(null, document.title, location.href);
		stepper.goBack();
	}
});


onMounted(() => {
	scrollToTop();

	if (isMobile.value) {
		if(!mobileSteps.value) return;
		let p = 20;
		if(document.querySelector('trenitalia-booking')) {
			p = document.querySelector('trenitalia-booking').getBoundingClientRect().left;
		}
		hostRef.value.style.marginLeft = '-' + p + 'px';
		hostRef.value.style.width = '100vw';

		hideMobileSteps(mobileSteps.value.stepsReference.children);

		setTimeout(function() {
			animateSteps(desktopSteps.value.stepsReference.children, mobileSteps.value.stepsReference.children);
		}, 1000);
	}
});
</script>

<style scoped>
.v-layout {
	flex-direction: column;
	min-height: 100%vh;
	position: relative;
	contain: content;
}

.v-footer {
	bottom: 0;
	left: 0;
	position: sticky;
	width: 100%;
}

#booking-footer {
	bottom: 0;
	left: 0;
	position: sticky;
	width: 100%;
}

.v-footer > .v-row {
	align-items: center;
}

.v-layout :deep(main .v-row) {
	margin-bottom: 20px;
}

.header-toolbar-background{
	box-shadow: 0 3px 6px 0 rgba(0, 0, 0, 0.08);
}
.remove {
	display: none !important;
}
</style>
